import React, { useState } from 'react';
import Immutable, { getIn } from 'seamless-immutable';
import { withRouter } from 'react-router';
import compose from 'lodash/fp/compose';
import { withCustomer } from '@catalogo/core-connect/customer';
import { withMetrics } from '@catalogo/core-metrics';
import { useNormalizedReviewData } from '../hooks';
import { formatRatingDistribution } from '../utils';
import withFilter from './withFilter';

const withReviewProduct = Component => ({ productId, reviewsQuery }) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { loading, data, fetchMore } = reviewsQuery;
  const reviewsData = getIn(data, ['product', 'reviews']);

  const reviews = getIn(reviewsData, ['result']) || [];
  const meta = getIn(reviewsData, ['meta']);
  const showLoadMore = meta && meta.total > meta.offset + meta.limit;
  const rating = getIn(data, ['product', 'rating']);
  const ratingDistribution = getIn(reviewsData, ['ratingDistribution']) || [];

  const { recommendedStore, ratingDistributionStore, hasReviews } = useNormalizedReviewData({
    reviews,
    recommended: getIn(reviewsData, ['recommended'], 0),
    ratingDistribution: formatRatingDistribution(ratingDistribution),
  });

  const loadMoreReviews = filters => {
    const { filterRating, filterSort } = filters;
    setIsLoadingMore(true);
    fetchMore({
      variables: { offset: meta.offset + meta.limit, productId, filters: filterRating, sort: filterSort },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        const prevReviews = getIn(prevResult, ['product', 'reviews', 'result']);
        const newReviews = getIn(fetchMoreResult, ['product', 'reviews', 'result'], []);
        const reviews = [...prevReviews, ...newReviews];
        setIsLoadingMore(false);
        return Immutable(fetchMoreResult).setIn(['product', 'reviews', 'result'], reviews).asMutable({ deep: true });
      },
      notifyOnNetworkStatusChange: false,
    });
  };

  if (!data) {
    return null;
  }

  return (
    <Component
      productId={productId}
      reviews={reviews}
      loading={loading}
      fetchMore={fetchMore}
      loadMoreReviews={loadMoreReviews}
      isLoadingMore={isLoadingMore}
      showLoadMore={showLoadMore}
      rating={rating}
      ratingDistribution={ratingDistributionStore}
      recommended={recommendedStore}
      hasReviewsStored={hasReviews}
    />
  );
};

export default compose(withRouter, withCustomer, withMetrics, withReviewProduct, withFilter);
export { withReviewProduct };
